import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import ListHeader from '@/common/components/ListHeader'
import Table from '@/common/components/Table'
import TextField from '@/common/components/TextField'
import { useEstablishmentRequiredDocuments } from '@/common/hooks/queries/useEstablishmentRequiredDocuments'
import useDebounce from '@/common/hooks/useDebounce'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { Mapper } from '@/common/services/mapper'
import {
  EstablishmentRequiredDocumentData,
  EstablishmentRequiredDocumentTableItem,
} from '@/common/types/establishmentRequiredDocument'
import { cn } from '@/common/utils/cn'
import { defaultColumns } from '@/features/maintenance/establishmentRequiredDocuments/constants/columns.constant'
import { EstablishmentRequiredDocumentsTransformer } from '@/features/maintenance/establishmentRequiredDocuments/services/establishmentRequiredDocumentsTransformer'
import { BookOpenTextIcon, SearchIcon } from 'lucide-react'

const List = () => {
  const { filterParams, setFilterParams } = useFilterParams()

  const [search, setSearch] = useDebounce(filterParams.search || '')

  const {
    data: establishmentRequiredDocuments,
    meta,
    isPending: establishmentRequiredDocumentsIsPending,
    error: establishmentRequiredDocumentsError = {} as any,
    refetch: refetchEstablishmentRequiredDocuments,
  } = useEstablishmentRequiredDocuments({
    filters: {
      search,
      page: filterParams.page || 1,
    },
    mapper: Mapper.mapWithTransformer<EstablishmentRequiredDocumentData[], EstablishmentRequiredDocumentTableItem[]>({
      transformer: EstablishmentRequiredDocumentsTransformer.tableTransformer(),
    }),
  })

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        Icon={<BookOpenTextIcon className="size-10" strokeWidth={1} />}
        title="Documentos Obrigatórios"
        createButtonText="Novo Documento"
        dataTestid="create_establishment_required_document_button"
        operationKlassToCheckPermission="Admin::Maintenance::EstablishmentRequiredDocuments::Create"
        disableCreateButton={
          establishmentRequiredDocumentsIsPending || establishmentRequiredDocumentsError?.status === 403
        }
      />

      <ErrorMessageWrapper
        className="h-[calc(100vh-128px)]"
        statusCode={establishmentRequiredDocumentsError?.status}
        refetchFunction={refetchEstablishmentRequiredDocuments}
      >
        <div className="h-[calc(100vh-128px)] flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <TextField
              name="search"
              LeftIcon={<SearchIcon />}
              placeholder="Pesquisar"
              className="rounded-sm shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              onChange={(e) => {
                setSearch((e.target as HTMLInputElement).value)
                setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }))
              }}
              value={filterParams.search}
            />
          </div>

          <Table
            className={cn('max-h-screen', {
              'shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]': !!establishmentRequiredDocuments?.length,
            })}
            columns={defaultColumns}
            isLoadingData={establishmentRequiredDocumentsIsPending}
            page={filterParams.page || 1}
            rows={establishmentRequiredDocuments}
            rowEmptyValue="--"
            totalPages={meta.total_pages}
            totalCount={meta.total}
            hasPagination
            setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  )
}

export default List
